import type { AudienceInput, AudienceKind, GroupRef, GroupSummary, LiveAudience } from '@ralph/contracts'
import clsx from 'clsx'
import { Check, ChevronDown, Globe, Lock, Search, UsersRound } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ReachItem } from '@/api/groups.ts'
import {
  optionMarks,
  type PickerSection,
  type PickerViewer,
  pickerSections,
  reachLine,
  reachPending,
  reviewLine
} from '@/components/groups/audience-picker.ts'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { GroupChip } from '@/components/groups/GroupLabel.tsx'
import { Spinner } from '@/components/ui/primitives.tsx'
import { useGroups, useReach } from '@/hooks/use-groups.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * "Who can use it" — one picker for an agent's audience, a knowledge base's access, and who may
 * use a connected system or a remote agent (PRD-26 D-30, GRP-101).
 *
 * ── The shape ──
 *
 * A fieldset of kind radios, each with a glyph and words (a lock, people, a globe — colour is
 * never the only carrier). Choosing "Specific groups" opens a token field: chosen groups as
 * chips, a search box, and the list of groups under it — inline rather than floating, because
 * the picker lives inside dialogs whose panels clip overflow, and a list cut off at the dialog's
 * edge is a list with rows nobody can reach.
 *
 * ── Keyboard ──
 *
 * The search box is a combobox over a multi-select listbox (`aria-activedescendant`, so focus
 * stays in the box while the arrows move through options). Enter toggles the active option and
 * never submits the form around it; Backspace in an empty box removes the last chip; Escape
 * closes the list and stops there, so it does not also close the dialog.
 *
 * ── The status line ──
 *
 * Under the picker, one polite live region says what the choice does: how many people it reaches
 * and how that differs from now (a distinct count from the server), and whether an administrator
 * will review it — predicted with the same rule the server decides with.
 */

export type AudiencePickerProps = {
  /** `item`: agents and knowledge bases (private · groups · everyone). `system`: everyone · groups. */
  mode: 'item' | 'system'
  value: AudienceInput
  onChange: (next: AudienceInput) => void
  /** The stored audience the review rule compares against — and, unless `inForce` says otherwise, what is live. Null for something new. */
  live: LiveAudience | null
  /** Who uses it now, when that differs from `live`: null for an agent that is not live, whose stored groups are not "shared now". */
  inForce?: LiveAudience | null
  /** Groups already known by name (the item's live audience), so chips render before any search. */
  known?: readonly GroupRef[]
  viewer: PickerViewer
  /** "Only me", or "Only Finance EMEA's managers". */
  privateLabel: string
  /** The item the reach line counts maintainers for, and compares against. */
  reachItem?: ReachItem
  /** What losing access costs, for "fewer than now". */
  consequence: string
  /** The review line's verb; systems have no review line (only administrators limit them). */
  commitVerb?: 'publish' | 'save'
  disabled?: boolean
  /** Rendered between the radios and the status line — e.g. coverage in the builder. */
  children?: ReactNode
  /**
   * The two sentences under the picker, exactly as shown, whenever they change. A caller that
   * stores what the person agreed to (an acknowledgement, a request's text) takes them from here
   * rather than rebuilding them — a group found only by typing is named here and nowhere else.
   */
  onStatus?: (status: { reach: string; review: string | null }) => void
}

const KIND_ICON: Record<AudienceKind, typeof Lock> = { private: Lock, groups: UsersRound, everyone: Globe }

export const AudiencePicker = ({
  mode,
  value,
  onChange,
  live,
  inForce,
  known = [],
  viewer,
  privateLabel,
  reachItem,
  consequence,
  commitVerb,
  disabled = false,
  children,
  onStatus
}: AudiencePickerProps) => {
  const fmt = useI18n()
  const t = fmt.t.groups.picker
  const legendId = useId()
  const statusId = useId()
  const kinds: AudienceKind[] = mode === 'system' ? ['everyone', 'groups'] : ['private', 'groups', 'everyone']

  // Every group this picker has ever seen, so a chip keeps its name and avatar after the search
  // that found it has been replaced by another.
  const [seen, setSeen] = useState<Map<string, GroupRef>>(() => new Map(known.map((group) => [group.id, group])))
  useEffect(() => {
    setSeen((current) => {
      if (known.every((group) => current.has(group.id))) return current
      const next = new Map(current)
      for (const group of known) next.set(group.id, group)
      return next
    })
  }, [known])

  const remember = (groups: readonly GroupRef[]) =>
    setSeen((current) => {
      if (groups.every((group) => current.has(group.id))) return current
      const next = new Map(current)
      for (const group of groups) next.set(group.id, group)
      return next
    })

  const chosenIds = value.kind === 'groups' ? value.groupIds : []

  const setKind = (kind: AudienceKind) => {
    if (kind === value.kind) return
    // Switching away from groups and back keeps the choice: the groups are part of what the
    // person was building, not a side effect of the radio.
    onChange(kind === 'groups' ? { kind, groupIds: lastGroups.current } : { kind, groupIds: [] })
  }
  const lastGroups = useRef<string[]>(chosenIds)
  useEffect(() => {
    if (value.kind === 'groups') lastGroups.current = value.groupIds
  }, [value])

  const toggle = (group: GroupRef) => {
    remember([group])
    const next = chosenIds.includes(group.id) ? chosenIds.filter((id) => id !== group.id) : [...chosenIds, group.id]
    onChange({ kind: 'groups', groupIds: next })
  }

  // ---- reach and review -----------------------------------------------------

  const valid = !(value.kind === 'groups' && value.groupIds.length === 0)
  const reach = useReach(valid ? value : null, reachItem)
  const names = useMemo(() => new Map([...seen.values()].map((group) => [group.id, group.name])), [seen])

  const reachText = fmt.t.groups.reach(
    fmt,
    reachPending(reach) && valid
      ? { kind: 'pending' }
      : reachLine({ requested: value, people: reach.data?.people ?? null, current: reach.data?.current ?? null }),
    { privateLabel, consequence }
  )
  const reviewText =
    commitVerb === undefined
      ? null
      : fmt.t.groups.review(fmt, reviewLine({ viewer, live, requested: value, names }), commitVerb)

  // Through a ref, so a caller passing an inline function does not re-fire this on every render.
  const statusListener = useRef(onStatus)
  statusListener.current = onStatus
  useEffect(() => {
    statusListener.current?.({ reach: reachText, review: reviewText })
  }, [reachText, reviewText])

  return (
    <fieldset aria-labelledby={legendId} aria-describedby={statusId} disabled={disabled} className="min-w-0">
      {/* Every place the picker appears already heads it with these words, so the legend only
          names the radio group for assistive technology. */}
      <legend id={legendId} className="sr-only">
        {t.legend}
      </legend>

      <div className={clsx('grid gap-2', kinds.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        {kinds.map((kind) => {
          const Icon = KIND_ICON[kind]
          const copy =
            mode === 'system'
              ? t.systemKinds[kind as 'everyone' | 'groups']
              : {
                  label: kind === 'private' ? t.kinds.private.label(privateLabel) : t.kinds[kind].label,
                  hint: t.kinds[kind].hint
                }
          const picked = value.kind === kind
          return (
            <label
              key={kind}
              className={clsx(
                // `relative`, because the radio inside is `sr-only` and that is `position:
                // absolute`: the card has to be what it is positioned against, so the control
                // sits where its card is, scrolls with it, and is what a focus brings into
                // view. Left to find its own containing block it found the stage panel, and
                // choosing an audience scrolled the whole page out of its frame. The builder, a
                // knowledge base and a connected system all host this picker, each inside a
                // different scroller, so it does not leave the matter to its host.
                'relative flex cursor-pointer items-start gap-2.5 rounded-tile border px-3 py-2.5',
                'transition-[background-color,border-color,box-shadow] duration-200',
                // The radio itself is visually hidden, so its keyboard focus shows on the card.
                'has-[:focus-visible]:shadow-[var(--ring)]',
                picked
                  ? 'border-primary bg-card-solid shadow-glass'
                  : 'border-rule bg-card-solid/55 hover:border-rule-strong',
                disabled && 'cursor-not-allowed opacity-45'
              )}
            >
              <input
                type="radio"
                name={legendId}
                value={kind}
                checked={picked}
                onChange={() => setKind(kind)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={clsx(
                  'mt-[1px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full',
                  picked ? 'bg-primary-soft text-primary' : 'bg-card text-fg-subtle'
                )}
              >
                <Icon size={12} strokeWidth={2.2} />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-[13px] font-semibold tracking-[-0.014em]">
                  {copy.label}
                  {picked && <Check size={12} aria-hidden className="text-primary" />}
                </span>
                <span className="mt-0.5 block text-pretty text-[12px] leading-[1.45] text-fg-muted">{copy.hint}</span>
              </span>
            </label>
          )
        })}
      </div>

      {value.kind === 'groups' && (
        <GroupCombobox
          chosenIds={chosenIds}
          seen={seen}
          live={live}
          inForce={inForce}
          viewer={viewer}
          reviewed={commitVerb !== undefined}
          disabled={disabled}
          onToggle={toggle}
          onSeen={remember}
        />
      )}

      {children}

      <div id={statusId} aria-live="polite" className="mt-3 space-y-1 text-[12.5px] leading-[1.5]">
        <p className="flex items-start gap-1.5 text-fg-muted">
          <UsersRound size={12} aria-hidden className="mt-[3px] shrink-0 text-fg-subtle" />
          <span>{reachText}</span>
        </p>
        {reviewText && (
          <p className="flex items-start gap-1.5 text-fg">
            <Check size={12} aria-hidden className="mt-[3px] shrink-0 text-primary" />
            <span>{reviewText}</span>
          </p>
        )}
      </div>
    </fieldset>
  )
}

// ---------------------------------------------------------------------------
// The token field and its list
// ---------------------------------------------------------------------------

const useDebounced = <T,>(value: T, ms: number): T => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}

type FlatOption = { group: GroupSummary; section: PickerSection['key'] | 'results' }

const GroupCombobox = ({
  chosenIds,
  seen,
  live,
  inForce,
  viewer,
  reviewed,
  disabled,
  onToggle,
  onSeen
}: {
  chosenIds: readonly string[]
  seen: ReadonlyMap<string, GroupRef>
  live: LiveAudience | null
  inForce: LiveAudience | null | undefined
  viewer: PickerViewer
  reviewed: boolean
  disabled: boolean
  onToggle: (group: GroupRef) => void
  onSeen: (groups: readonly GroupRef[]) => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.groups.picker
  const inputId = useId()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(chosenIds.length === 0)
  const [active, setActive] = useState(0)
  const debounced = useDebounced(query.trim(), 180)

  // With a query, the server ranks (exact, prefix, word prefix, anywhere); without one, the first
  // two hundred by name, split into sections here.
  const list = useGroups(debounced === '' ? { limit: 200 } : { q: debounced, limit: 50 })
  const items = list.data?.items ?? []

  useEffect(() => {
    if (items.length > 0) onSeen(items)
  }, [items, onSeen])

  const sections = useMemo(() => (debounced === '' ? pickerSections(items) : []), [debounced, items])
  const flat: FlatOption[] = useMemo(
    () =>
      debounced === ''
        ? sections.flatMap((section) => section.groups.map((group) => ({ group, section: section.key })))
        : items.map((group) => ({ group, section: 'results' as const })),
    [debounced, sections, items]
  )

  // The active option follows the list as it changes, and never points past its end.
  useEffect(() => {
    setActive((current) => Math.min(current, Math.max(flat.length - 1, 0)))
  }, [flat.length])

  const optionId = (index: number) => `${listId}-option-${index}`
  const activeOption = flat[active]

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((current) => (flat.length === 0 ? 0 : (current + step + flat.length) % flat.length))
      return
    }
    if (event.key === 'Home' && open) {
      event.preventDefault()
      setActive(0)
      return
    }
    if (event.key === 'End' && open) {
      event.preventDefault()
      setActive(Math.max(flat.length - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      // Never submits the form around the picker: Enter here means "this one".
      event.preventDefault()
      if (open && activeOption) onToggle(activeOption.group)
      else setOpen(true)
      return
    }
    if (event.key === 'Escape' && open) {
      // Closes the list and stops there, so the dialog around the picker stays open.
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      return
    }
    if (event.key === 'Backspace' && query === '' && chosenIds.length > 0) {
      const last = seen.get(chosenIds[chosenIds.length - 1] ?? '')
      if (last) onToggle(last)
    }
  }

  const announce = list.isLoading
    ? t.loading
    : list.isError
      ? t.failed
      : debounced !== '' && items.length === 0
        ? t.noMatch(debounced)
        : debounced !== ''
          ? t.results(fmt, list.data?.total ?? items.length)
          : ''

  const renderOption = (option: FlatOption, index: number) => {
    const { group } = option
    const selected = chosenIds.includes(group.id)
    const marks = optionMarks(group, { live, inForce, viewer, reviewed })
    return (
      // A button so a pointer can press it; `tabIndex={-1}` because focus stays in the combobox,
      // which reaches the option with aria-activedescendant — the command palette's pattern.
      <button
        key={group.id}
        id={optionId(index)}
        type="button"
        role="option"
        tabIndex={-1}
        aria-selected={selected}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => onToggle(group)}
        onMouseMove={() => setActive(index)}
        className={clsx(
          'flex w-full cursor-pointer items-center gap-2.5 rounded-chip px-2 py-1.5 text-left',
          index === active ? 'bg-card shadow-[inset_0_0_0_1px_var(--rule-strong)]' : 'hover:bg-card'
        )}
      >
        <span
          aria-hidden
          className={clsx(
            'flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[5px] border',
            selected ? 'border-primary bg-primary text-white' : 'border-rule-strong bg-card-solid'
          )}
        >
          {selected && <Check size={10} strokeWidth={3} />}
        </span>
        <GroupAvatar group={group} size={28} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold tracking-[-0.012em]">{group.name}</span>
          <span className="readout block truncate text-fg-subtle">
            {t.optionMeta(fmt, group.memberCount, group.isManager, group.isMember)}
          </span>
        </span>
        {marks.map((mark) => (
          <span
            key={mark}
            className={clsx(
              'shrink-0 rounded-full px-1.5 py-[1px] font-mono text-[9px] uppercase tracking-[0.08em]',
              mark === 'shared_now' ? 'bg-grounded-soft text-grounded' : 'bg-primary-soft text-primary'
            )}
          >
            {t.marks[mark]}
          </span>
        ))}
      </button>
    )
  }

  let index = -1
  return (
    <div className="mt-3">
      {chosenIds.length > 0 && (
        <ul aria-label={t.chosenLabel} className="mb-2 flex flex-wrap gap-1.5">
          {chosenIds.map((id) => {
            const group = seen.get(id)
            if (!group) return null
            const marks = optionMarks(
              { id, isManager: viewer.managedGroupIds.includes(id) },
              { live, inForce, viewer, reviewed }
            )
            return (
              <li key={id} className="max-w-full">
                <GroupChip
                  group={group}
                  disabled={disabled}
                  onRemove={() => onToggle(group)}
                  removeLabel={t.remove(group.name)}
                  {...(marks.includes('needs_review')
                    ? { mark: { text: t.marks.needs_review, tone: 'primary' as const } }
                    : {})}
                />
              </li>
            )
          })}
        </ul>
      )}

      <div className="relative">
        <label htmlFor={inputId} className="sr-only">
          {t.searchLabel}
        </label>
        <Search size={13} aria-hidden className="pointer-events-none absolute left-3 top-[10px] text-fg-subtle" />
        <input
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeOption ? optionId(active) : undefined}
          value={query}
          disabled={disabled}
          placeholder={t.searchPlaceholder}
          autoComplete="off"
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
            setActive(0)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="h-[34px] w-full rounded-tile border border-rule-strong bg-card-solid pl-8 pr-9 text-[13px] text-fg placeholder:text-fg-subtle focus:border-primary focus:outline-none"
        />
        <button
          type="button"
          aria-label={open ? t.hideList : t.showList}
          aria-controls={listId}
          aria-expanded={open}
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((current) => !current)}
          className="absolute right-1.5 top-1/2 flex h-[24px] w-[24px] -translate-y-1/2 items-center justify-center rounded-full text-fg-subtle hover:text-fg"
        >
          <ChevronDown size={13} aria-hidden className={clsx('transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      <p aria-live="polite" className="sr-only">
        {announce}
      </p>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-multiselectable
          aria-label={t.legend}
          className="mt-1.5 max-h-[17.5rem] overflow-y-auto rounded-tile border border-rule bg-card-solid p-1 shadow-glass"
        >
          {list.isLoading && (
            <p className="flex items-center gap-2 px-2 py-2 text-[12.5px] text-fg-subtle">
              <Spinner /> {t.loading}
            </p>
          )}
          {list.isError && <p className="px-2 py-2 text-[12.5px] text-alert">{t.failed}</p>}
          {!list.isLoading && !list.isError && flat.length === 0 && (
            <p className="px-2 py-2 text-[12.5px] text-fg-muted">
              {debounced === '' ? t.noGroups : t.noMatch(debounced)}
            </p>
          )}

          {debounced === ''
            ? sections.map((section) => {
                const headingId = `${listId}-${section.key}`
                return (
                  // biome-ignore lint/a11y/useSemanticElements: a titled set of options inside a listbox is an ARIA group; a fieldset would announce a form section.
                  <div key={section.key} role="group" aria-labelledby={headingId} className="py-0.5">
                    <p id={headingId} role="presentation" className="eyebrow px-2 pb-1 pt-1.5">
                      {sections.length === 1 && section.key === 'other' ? t.allGroups : t.sections[section.key]}
                    </p>
                    {section.groups.map((group) => {
                      index += 1
                      return renderOption({ group, section: section.key }, index)
                    })}
                  </div>
                )
              })
            : flat.map((option, position) => renderOption(option, position))}
        </div>
      )}
    </div>
  )
}
